import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import authService from '../../utils/authService';

const TOTPEnrollmentModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('loading'); // loading, qr, verify, success
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Start enrollment when modal opens
  useEffect(() => {
    if (isOpen) {
      startEnrollment();
    } else {
      // Reset state when modal closes
      setStep('loading');
      setEnrollmentData(null);
      setVerifyCode('');
      setError('');
      setShowSecret(false);
    }
  }, [isOpen]);

  const startEnrollment = async () => {
    setStep('loading');
    setError('');

    try {
      const result = await authService.enrollTOTP();

      if (!result.success) {
        setError(result.error || 'Failed to start enrollment');
        setStep('error');
        return;
      }

      setEnrollmentData(result.data);
      setStep('qr');
    } catch (err) {
      setError('An unexpected error occurred');
      setStep('error');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);

    try {
      const result = await authService.verifyTOTPEnrollment(
        enrollmentData.id,
        verifyCode
      );

      if (!result.success) {
        setError(result.error || 'Invalid code. Please try again.');
        setIsVerifying(false);
        return;
      }

      setStep('success');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError('An unexpected error occurred');
      setIsVerifying(false);
    }
  };

  const handleCancel = () => {
    setVerifyCode('');
    setError('');
    onClose();
  };

  const renderContent = () => {
    switch (step) {
      case 'loading':
        return (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-muted-foreground">Setting up two-factor authentication...</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Setup Failed</h3>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="flex justify-center space-x-3">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={startEnrollment}>
                Try Again
              </Button>
            </div>
          </div>
        );

      case 'qr':
        return (
          <div>
            <div className="mb-6">
              <div className="flex items-start space-x-3 p-4 bg-primary/5 border border-primary/20 rounded-lg mb-6">
                <Icon name="Info" size={20} className="text-primary mt-0.5" />
                <div className="text-sm">
                  <p className="text-foreground font-medium mb-1">Scan with your authenticator app</p>
                  <p className="text-muted-foreground">
                    Use apps like Google Authenticator, Authy, or 1Password to scan this QR code.
                  </p>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-lg inline-block">
                  <div
                    dangerouslySetInnerHTML={{ __html: enrollmentData.qrCode }}
                    className="w-64 h-64"
                  />
                </div>
              </div>

              {/* Manual Entry */}
              <div className="mb-6">
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="w-full flex items-center justify-between p-4 bg-muted/20 rounded-lg hover:bg-muted/30 transition-colors"
                >
                  <span className="text-sm font-medium text-foreground">
                    Can't scan? Enter manually
                  </span>
                  <Icon
                    name={showSecret ? 'ChevronUp' : 'ChevronDown'}
                    size={20}
                    className="text-muted-foreground"
                  />
                </button>

                {showSecret && (
                  <div className="mt-3 p-4 bg-muted/20 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-2">Secret Key:</p>
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-mono text-foreground break-all">
                        {enrollmentData.secret}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Copy"
                        onClick={() => {
                          navigator.clipboard.writeText(enrollmentData.secret);
                        }}
                        className="ml-2"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Verification Form */}
              <form onSubmit={handleVerify} className="space-y-4">
                <Input
                  label="Enter 6-digit code"
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerifyCode(value);
                    setError('');
                  }}
                  error={error}
                  maxLength={6}
                  autoFocus
                  description="Enter the 6-digit code from your authenticator app"
                />

                <div className="flex items-center justify-end space-x-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isVerifying}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={verifyCode.length !== 6}
                    loading={isVerifying}
                  >
                    Verify & Enable
                  </Button>
                </div>
              </form>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Check" size={32} className="text-accent" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Success!</h3>
            <p className="text-muted-foreground">
              Two-factor authentication has been enabled for your account.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === 'loading' || step === 'success' ? () => {} : handleCancel}
      title={step === 'qr' ? 'Enable Two-Factor Authentication' : null}
      size="md"
      showCloseButton={step !== 'loading' && step !== 'success'}
    >
      {renderContent()}
    </Modal>
  );
};

export default TOTPEnrollmentModal;
